import React, { useEffect, useState } from 'react';
import {
    Box, Typography, LinearProgress, Paper, Tooltip, IconButton,
    useTheme, Dialog, DialogTitle, DialogContent, DialogActions,
    Button, Divider, Chip, TextField, Alert, CircularProgress, Grid,
    Skeleton, Accordion, AccordionSummary, AccordionDetails,
    MenuItem, ListItemIcon, ListItemText
} from '@mui/material';
import {
    InfoOutlined as InfoIcon,
    Speed as SpeedIcon,
    Article as ArticleIcon,
    Business as BrandIcon,
    AutoAwesome as AIIcon,
    CalendarToday as CalendarIcon,
    CardGiftcard as TokenIcon,
    CheckCircle as CheckIcon,
    Close as CloseIcon,
    Upgrade as UpgradeIcon,
    Key as KeyIcon,
    History as HistoryIcon,
    ExpandMore as ExpandMoreIcon,
    DeleteOutline as DeleteIcon
} from '@mui/icons-material';
import api from '../../../services/api';
import { useAuth } from '../../../context/AuthContext';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import PlatformDialog from '../PlatformDialog/PlatformDialog';
import PlatformDialogTitle from '../PlatformDialog/PlatformDialogTitle';
import MIAToggle from '../MIAToggle';

const UsageCounter = ({ collapsed = false, variant = 'card' }) => {
    const [status, setStatus] = useState(null);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const [myTokens, setMyTokens] = useState([]);
    const [tokensLoading, setTokensLoading] = useState(false);
    const theme = useTheme();
    const { user, updateUser } = useAuth();

    // Token de upgrade
    const [upgradeToken, setUpgradeToken] = useState('');
    const [upgradeLoading, setUpgradeLoading] = useState(false);
    const [upgradeError, setUpgradeError] = useState('');
    const [upgradeSuccess, setUpgradeSuccess] = useState('');

    const fetchStatus = async () => {
        try {
            const res = await api.get('/auth/activation-status');
            if (res.data.success) setStatus(res.data.data);
        } catch (error) {
            console.error('Erro ao buscar status de uso:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchMyTokens = async () => {
        setTokensLoading(true);
        try {
            const res = await api.get('/mia/my-tokens');
            if (res.data.success) setMyTokens(res.data.tokens || []);
        } catch (err) {
            console.error('Erro ao buscar tokens:', err);
        } finally {
            setTokensLoading(false);
        }
    };

    const handleOpen = () => {
        setOpen(true);
        fetchMyTokens();
    };

    useEffect(() => {
        fetchStatus();
        const interval = setInterval(fetchStatus, 5 * 60 * 1000);

        // Atualiza imediatamente quando um artigo é criado
        const handleRefresh = () => fetchStatus();
        window.addEventListener('refresh-usage-counter', handleRefresh);

        return () => {
            clearInterval(interval);
            window.removeEventListener('refresh-usage-counter', handleRefresh);
        };
    }, []);

    const handleTokenChange = (e) => {
        const raw = e.target.value.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
        const groups = raw.match(/.{1,4}/g) || [];
        setUpgradeToken(groups.join('-').slice(0, 19));
        setUpgradeError('');
        setUpgradeSuccess('');
    };

    const handleRedeemToken = async () => {
        if (!upgradeToken.trim()) return;
        setUpgradeLoading(true);
        setUpgradeError('');
        setUpgradeSuccess('');
        try {
            const response = await api.post('/mia/activate', { token: upgradeToken.trim() });
            if (response.data.success) {
                const newPlan = response.data.plan || response.data.data?.plan;
                setUpgradeSuccess(response.data.message || `Plano ${newPlan?.name || ''} aplicado!`);
                setUpgradeToken('');
                updateUser({
                    ...user,
                    activation: { ...(user?.activation || {}), plan: newPlan, isActivated: true }
                });
                fetchStatus(); // atualiza o contador
            }
        } catch (err) {
            setUpgradeError(err.response?.data?.message || err.response?.data?.error || 'Token inválido ou já utilizado');
        } finally {
            setUpgradeLoading(false);
        }
    };

    if (loading || !status || !status.plan || status.role === 'admin' || status.role === 'mensure') {
        return null;
    }

    const { plan, usage } = status;
    const maxLinks = plan.maxLinks || 0;
    const monthlyLinks = usage.monthlyLinks ?? 0;           // publicações neste mês
    const linksUsed = monthlyLinks;                         // alias para progress bar
    const monthlyResetAt = usage.monthlyResetAt ? new Date(usage.monthlyResetAt) : null;
    const deletedLinks = usage.deletedLinks ?? 0;
    const deletedLinksByBrand = usage.deletedLinksByBrand ?? [];
    const brandsUsage = usage.brandsUsage ?? [];
    const nextReset = monthlyResetAt
        ? new Date(monthlyResetAt.getFullYear(), monthlyResetAt.getMonth() + 1, 1)
        : null;
    const percentage = maxLinks > 0 ? Math.min(Math.round((linksUsed / maxLinks) * 100), 100) : 0;
    const isCritical = percentage >= 90;
    const isWarning = percentage >= 75;

    const getProgressColor = () => {
        if (isCritical) return theme.palette.error.main;
        if (isWarning) return theme.palette.warning.main;
        return '#4ade80';
    };

    const planExpiresAt = plan.planExpiresAt ? new Date(plan.planExpiresAt) : null;
    const formatDate = (d) => d ? format(d, 'dd/MM/yyyy', { locale: ptBR }) : '—';

    if (variant === 'menuItem') {
        return (
            <>
                <MenuItem onClick={handleOpen}>
                    <ListItemIcon>
                        <SpeedIcon fontSize="small" sx={{ color: getProgressColor() }} />
                    </ListItemIcon>
                    <ListItemText
                        primary={`Plano ${plan.name}`}
                        secondary={`${linksUsed} de ${maxLinks} artigos (${percentage}%)`}
                        primaryTypographyProps={{ fontWeight: 700 }}
                    />
                </MenuItem>
                <PlanDialog
                    open={open} onClose={() => setOpen(false)}
                    plan={plan} linksUsed={linksUsed} maxLinks={maxLinks}
                    percentage={percentage} isCritical={isCritical} isWarning={isWarning}
                    getProgressColor={getProgressColor} planExpiresAt={planExpiresAt} formatDate={formatDate}
                    upgradeToken={upgradeToken} upgradeLoading={upgradeLoading}
                    upgradeError={upgradeError} upgradeSuccess={upgradeSuccess}
                    onTokenChange={handleTokenChange} onRedeem={handleRedeemToken}
                    myTokens={myTokens} tokensLoading={tokensLoading}
                    nextReset={nextReset}
                    deletedLinks={deletedLinks} brandsUsage={brandsUsage} deletedLinksByBrand={deletedLinksByBrand}
                />
            </>
        );
    }

    if (collapsed) {
        return (
            <>
                <Tooltip title={`${linksUsed} de ${maxLinks} artigos · Clique para detalhes`} placement="right">
                    <Box
                        onClick={handleOpen}
                        sx={{ display: 'flex', justifyContent: 'center', py: 2, cursor: 'pointer' }}
                    >
                        <SpeedIcon sx={{ color: getProgressColor(), opacity: 0.8 }} />
                    </Box>
                </Tooltip>
                <PlanDialog
                    open={open} onClose={() => setOpen(false)}
                    plan={plan} linksUsed={linksUsed} maxLinks={maxLinks}
                    percentage={percentage} isCritical={isCritical} isWarning={isWarning}
                    getProgressColor={getProgressColor} planExpiresAt={planExpiresAt} formatDate={formatDate}
                    upgradeToken={upgradeToken} upgradeLoading={upgradeLoading}
                    upgradeError={upgradeError} upgradeSuccess={upgradeSuccess}
                    onTokenChange={handleTokenChange} onRedeem={handleRedeemToken}
                    myTokens={myTokens} tokensLoading={tokensLoading}
                    nextReset={nextReset}
                    deletedLinks={deletedLinks} brandsUsage={brandsUsage} deletedLinksByBrand={deletedLinksByBrand}
                />
            </>
        );
    }

    return (
        <>
            <Paper
                elevation={0}
                onClick={handleOpen}
                sx={{
                    mx: 2, mb: 2, p: 2,
                    background: 'rgba(255, 255, 255, 0.03)',
                    border: '1px solid rgba(255, 255, 255, 0.08)',
                    borderRadius: 2,
                    cursor: 'pointer',
                    transition: 'border-color 0.2s, background 0.2s',
                    '&:hover': {
                        background: 'rgba(255, 255, 255, 0.06)',
                        borderColor: 'rgba(255,255,255,0.18)'
                    }
                }}
            >
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        Plano {plan.name}
                    </Typography>
                    <Tooltip title="Ver detalhes e resgatar token">
                        <TokenIcon sx={{ fontSize: 14, color: 'text.secondary', opacity: 0.7 }} />
                    </Tooltip>
                </Box>

                <Box mb={0.5}>
                    <Typography variant="body2" fontWeight={600} color={isCritical ? 'error.main' : 'text.primary'}>
                        {linksUsed} <Typography component="span" variant="caption" color="text.secondary">/ {maxLinks} artigos</Typography>
                    </Typography>
                </Box>

                <LinearProgress
                    variant="determinate"
                    value={percentage}
                    sx={{
                        height: 6, borderRadius: 3,
                        bgcolor: 'rgba(255, 255, 255, 0.05)',
                        '& .MuiLinearProgress-bar': { bgcolor: getProgressColor(), borderRadius: 3 }
                    }}
                />

                <Box display="flex" justifyContent="end" mt={0.5}>
                    <Typography variant="caption" color={isCritical ? 'error.main' : 'text.secondary'} fontWeight={isCritical ? 700 : 400}>
                        {percentage}%
                    </Typography>
                </Box>
            </Paper>

            <PlanDialog
                open={open} onClose={() => setOpen(false)}
                plan={plan} linksUsed={linksUsed} maxLinks={maxLinks}
                percentage={percentage} isCritical={isCritical} isWarning={isWarning}
                getProgressColor={getProgressColor} planExpiresAt={planExpiresAt} formatDate={formatDate}
                upgradeToken={upgradeToken} upgradeLoading={upgradeLoading}
                upgradeError={upgradeError} upgradeSuccess={upgradeSuccess}
                onTokenChange={handleTokenChange} onRedeem={handleRedeemToken}
                myTokens={myTokens} tokensLoading={tokensLoading}
                nextReset={nextReset}
                deletedLinks={deletedLinks} brandsUsage={brandsUsage} deletedLinksByBrand={deletedLinksByBrand}
            />
        </>
    );
};

// ── Dialog de detalhes do plano + resgate de token ──────────────────────────
const PlanDialog = ({
    open, onClose, plan, linksUsed, maxLinks, percentage,
    isCritical, isWarning, getProgressColor, planExpiresAt, formatDate,
    upgradeToken, upgradeLoading, upgradeError, upgradeSuccess,
    onTokenChange, onRedeem,
    myTokens = [], tokensLoading = false,
    nextReset = null,
    deletedLinks = 0, brandsUsage = [], deletedLinksByBrand = []
}) => {
    const theme = useTheme();
    const { user } = useAuth();
    const isDarkMode = theme.palette.mode === 'dark';

    return (
        <PlatformDialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <PlatformDialogTitle
                title="Meu Plano"
                subtitle={`Conta ${user?.email || 'padrão'}`}
                icon={<UpgradeIcon fontSize="small" />}
                onClose={onClose}
            />

            <DialogContent sx={{ px: 3, pt: 3.5, pb: 3 }}>
                {/* MIA Toggle movido para cá */}
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                    <MIAToggle />
                </Box>

                {/* Cards de métricas */}
                <Grid container spacing={1.5} mb={3}>
                    <Grid item xs={6} sm={3}>
                        <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: isDarkMode ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)', textAlign: 'center', border: '1px solid', borderColor: isDarkMode ? 'rgba(255,255,255,0.08)' : 'divider' }}>
                            <ArticleIcon sx={{ color: 'primary.main', fontSize: 20, mb: 0.5 }} />
                            <Typography variant="h6" fontWeight={800} color="text.primary" sx={{ lineHeight: 1 }}>{maxLinks}</Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>Artigos/Mês</Typography>
                        </Box>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                        <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: isDarkMode ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)', textAlign: 'center', border: '1px solid', borderColor: isDarkMode ? 'rgba(255,255,255,0.08)' : 'divider' }}>
                            <BrandIcon sx={{ color: 'secondary.main', fontSize: 20, mb: 0.5 }} />
                            <Typography variant="h6" fontWeight={800} color="text.primary" sx={{ lineHeight: 1 }}>{plan.maxBrands ?? '—'}</Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>Marcas</Typography>
                        </Box>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                        <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: isDarkMode ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)', textAlign: 'center', border: '1px solid', borderColor: isDarkMode ? 'rgba(255,255,255,0.08)' : 'divider' }}>
                            <AIIcon sx={{ color: plan.hasAI ? 'success.main' : 'text.disabled', fontSize: 20, mb: 0.5 }} />
                            <Typography variant="body1" fontWeight={800} color={plan.hasAI ? 'success.main' : 'text.disabled'} sx={{ lineHeight: 1 }}>
                                {plan.hasAI ? 'Ativa' : 'Inativa'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>IA Premium</Typography>
                        </Box>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                        <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: isDarkMode ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)', textAlign: 'center', border: '1px solid', borderColor: isDarkMode ? 'rgba(255,255,255,0.08)' : 'divider' }}>
                            <CalendarIcon sx={{ color: 'info.main', fontSize: 20, mb: 0.5 }} />
                            <Typography variant="body1" fontWeight={800} color="text.primary" sx={{ lineHeight: 1, fontSize: '0.8rem' }}>{formatDate(planExpiresAt)}</Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>Expira em</Typography>
                        </Box>
                    </Grid>
                </Grid>

                {/* Uso atual - mês corrente */}
                <Box mb={2}>
                    <Box display="flex" justifyContent="space-between" mb={0.5}>
                        <Box>
                            <Typography variant="body2" fontWeight={600}>
                                Este mês
                            </Typography>
                            {nextReset && (
                                <Typography variant="caption" color="text.secondary">
                                    Renova em {formatDate(nextReset)}
                                </Typography>
                            )}
                        </Box>
                        <Typography variant="body2" color={isCritical ? 'error.main' : 'text.primary'} fontWeight={700}>
                            {linksUsed} / {maxLinks} pub. ({percentage}%)
                        </Typography>
                    </Box>
                    <LinearProgress
                        variant="determinate"
                        value={percentage}
                        sx={{
                            height: 8, borderRadius: 4,
                            bgcolor: 'action.selected',
                            '& .MuiLinearProgress-bar': { bgcolor: getProgressColor(), borderRadius: 4 }
                        }}
                    />
                    {isCritical && (
                        <Alert severity="error" sx={{ mt: 1, py: 0.5 }}>
                            Limite quase atingido! Entre em contato com a Mensure para fazer upgrade.
                        </Alert>
                    )}
                </Box>

                {/* Consumo por marca - Accordion */}
                {(brandsUsage.length > 0 || deletedLinks > 0 || deletedLinksByBrand.length > 0) && (
                    <Accordion
                        disableGutters
                        elevation={0}
                        sx={{
                            mb: 2,
                            borderRadius: 2,
                            border: '1px solid',
                            borderColor: 'divider',
                            bgcolor: 'action.hover',
                            '&:before': { display: 'none' },
                            '&.Mui-expanded': { mb: 2 }
                        }}
                    >
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon sx={{ fontSize: 18 }} />}
                            sx={{ minHeight: 40, '& .MuiAccordionSummary-content': { my: 0.5 }, px: 1.5 }}
                        >
                            <Box display="flex" alignItems="center" gap={0.75}>
                                <BrandIcon sx={{ fontSize: 16, color: 'primary.light' }} />
                                <Typography variant="body2" fontWeight={700} color="primary.light">
                                    Consumo por marca
                                </Typography>
                                <Chip
                                    label={`${brandsUsage.reduce((s, b) => s + b.count, 0)} pub`}
                                    size="small"
                                    sx={{ height: 18, fontSize: '0.65rem', fontWeight: 700, ml: 0.5 }}
                                />
                            </Box>
                        </AccordionSummary>
                        <AccordionDetails sx={{ pt: 0, pb: 1.5, px: 1.5 }}>
                            {brandsUsage.map(b => (
                                <Box key={b.brandId} display="flex" alignItems="center" justifyContent="space-between" py={0.4}>
                                    <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: '65%' }}>{b.brandName}</Typography>
                                    <Chip label={`${b.count} pub`} size="small" color="primary" variant="outlined" sx={{ fontWeight: 700, fontSize: '0.7rem' }} />
                                </Box>
                            ))}
                            {/* Excluídos por marca */}
                            {deletedLinksByBrand.length > 0 && (
                                <Box mt={brandsUsage.length > 0 ? 1 : 0}>
                                    <Typography variant="caption" color="error.light" fontWeight={600} display="block" mb={0.5}>
                                        🗑 Excluídos por marca
                                    </Typography>
                                    {deletedLinksByBrand.map((b, i) => (
                                        <Box key={b.brandId || i} display="flex" alignItems="center" justifyContent="space-between" py={0.3}>
                                            <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: '65%' }}>{b.brandName}</Typography>
                                            <Chip label={`${b.count} art.`} size="small" color="error" variant="outlined" sx={{ fontWeight: 700, fontSize: '0.7rem' }} />
                                        </Box>
                                    ))}
                                </Box>
                            )}
                            {/* Fallback total sem detalhamento */}
                            {deletedLinks > 0 && deletedLinksByBrand.length === 0 && (
                                <Box display="flex" alignItems="center" justifyContent="space-between" mt={0.5}>
                                    <Box display="flex" alignItems="center" gap={0.5}>
                                        <DeleteIcon sx={{ fontSize: 14, color: 'error.light' }} />
                                        <Typography variant="body2" color="error.light">Excluídos (total)</Typography>
                                    </Box>
                                    <Chip label={`${deletedLinks} art.`} size="small" color="error" variant="outlined" sx={{ fontWeight: 700, fontSize: '0.7rem' }} />
                                </Box>
                            )}
                        </AccordionDetails>
                    </Accordion>
                )}

                <Divider sx={{ mb: 2.5 }} />

                {/* Tokens Ativados */}
                <Box mb={2.5}>
                    <Typography variant="subtitle2" fontWeight={700} mb={1.5} display="flex" alignItems="center" gap={0.5}>
                        <HistoryIcon sx={{ fontSize: 18 }} /> Tokens Ativados
                    </Typography>

                    {tokensLoading ? (
                        [0, 1].map(i => <Skeleton key={i} variant="rounded" height={60} sx={{ mb: 1 }} />)
                    ) : myTokens.length === 0 ? (
                        <Typography variant="caption" color="text.secondary">Nenhum token encontrado.</Typography>
                    ) : (
                        <>
                            {myTokens.map((t) => {
                                const isRevoked = t.status === 'revoked';
                                return (
                                    <Box
                                        key={t._id}
                                        sx={{
                                            p: 1.5, mb: 1.5, borderRadius: 1.5,
                                            border: '1px solid',
                                            borderColor: isRevoked
                                                ? 'error.main'
                                                : (t.type === 'upgrade' ? 'warning.main' : 'primary.main'),
                                            bgcolor: isDarkMode ? 'rgba(0,0,0,0.2)' : 'rgba(0,0,0,0.02)',
                                            opacity: isRevoked ? 0.6 : 1,
                                            transition: 'transform 0.2s',
                                            '&:hover': { transform: 'translateX(4px)' }
                                        }}
                                    >
                                        <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                                            <Box display="flex" alignItems="center" gap={1}>
                                                {t.type === 'upgrade'
                                                    ? <UpgradeIcon sx={{ fontSize: 18, color: 'warning.main' }} />
                                                    : <KeyIcon sx={{ fontSize: 18, color: 'primary.main' }} />}
                                                <Typography variant="body2" fontWeight={700} color="text.primary">
                                                    {t.type === 'upgrade' ? 'Upgrade' : 'Ativação'} · {t.plan?.name || 'Mensure'}
                                                </Typography>
                                                {isRevoked && (
                                                    <Chip
                                                        label="Revogado"
                                                        size="small"
                                                        color="error"
                                                        sx={{ height: 18, fontSize: '0.65rem', fontWeight: 700 }}
                                                    />
                                                )}
                                            </Box>
                                            <Typography variant="caption" color="text.secondary">
                                                {t.activatedAt ? formatDate(new Date(t.activatedAt)) : '—'}
                                            </Typography>
                                        </Box>

                                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                                            <Typography 
                                                variant="caption" 
                                                sx={{ 
                                                    fontFamily: 'monospace', 
                                                    background: 'rgba(255,255,255,0.05)', 
                                                    px: 1, py: 0.3, 
                                                    borderRadius: 1,
                                                    letterSpacing: '0.05em' 
                                                }}
                                            >
                                                {t.token ? t.token.replace(/^(.{4})-.+?-(.{4})$/, '$1-••••-••••-$2') : '—'}
                                            </Typography>
                                            <Box display="flex" gap={1.5}>
                                                <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                    <ArticleIcon sx={{ fontSize: 12 }} /> {t.plan?.maxLinks ?? '—'}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                    <BrandIcon sx={{ fontSize: 12 }} /> {t.plan?.maxBrands ?? '—'}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Box>
                                );
                            })}
                        </>
                    )}
                </Box>

                <Divider sx={{ mb: 2.5 }} />

                {/* Resgate de token */}
                <Box>
                    <Typography variant="subtitle2" fontWeight={700} mb={0.5}>
                        <TokenIcon sx={{ verticalAlign: 'middle', mr: 0.5, fontSize: 18 }} />
                        Resgatar Token de Upgrade
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block" mb={1.5}>
                        Recebeu um token de upgrade da equipe Mensure? Insira abaixo para ampliar seus limites.
                    </Typography>

                    {upgradeError && <Alert severity="error" sx={{ mb: 1.5 }}>{upgradeError}</Alert>}
                    {upgradeSuccess && <Alert severity="success" icon={<CheckIcon />} sx={{ mb: 1.5 }}>{upgradeSuccess}</Alert>}

                    <Box display="flex" gap={1.5} alignItems="flex-start">
                        <TextField
                            placeholder="XXXX-XXXX-XXXX-XXXX"
                            value={upgradeToken}
                            onChange={onTokenChange}
                            onKeyDown={(e) => e.key === 'Enter' && onRedeem()}
                            size="small"
                            inputProps={{
                                style: { fontFamily: 'monospace', letterSpacing: '0.08em', fontSize: '0.95rem' },
                                maxLength: 19
                            }}
                            sx={{ flex: 1 }}
                        />
                        <Button
                            variant="contained"
                            onClick={onRedeem}
                            disabled={upgradeLoading || upgradeToken.replace(/-/g, '').length < 16}
                            sx={{ height: 40, minWidth: 130, whiteSpace: 'nowrap' }}
                            startIcon={upgradeLoading ? <CircularProgress size={14} color="inherit" /> : <TokenIcon />}
                        >
                            Resgatar
                        </Button>
                    </Box>
                </Box>
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 3, pt: 1, gap: 1.5 }}>
                <Button onClick={onClose} variant="text" sx={{ flex: 1 }}>
                    Fechar
                </Button>
                <Button
                    href={`https://wa.me/5511980464551?text=${encodeURIComponent('Olá! Gostaria de falar sobre upgrade de plano no Mensure Monitor.')}`}
                    target="_blank"
                    variant="contained"
                    fullWidth
                    sx={{
                        flex: 2,
                        background: 'linear-gradient(45deg, #25D366 30%, #128C7E 90%)',
                        color: 'white',
                        fontWeight: 700,
                        '&:hover': {
                            background: 'linear-gradient(45deg, #128C7E 30%, #075E54 90%)'
                        }
                    }}
                >
                    Falar com Mensure
                </Button>
            </DialogActions>
        </PlatformDialog >
    );
};

export default UsageCounter;
